import { useState, useRef } from "react";
import { Table, CombinedTable, Reservation } from "@/types/table";
import { TablePosition, TableShape, TableStatus, getStatusColor, LinkedTableInfo } from "@/lib/floorPlanUtils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import { Users, Clock, Phone, Mail, Edit, GripVertical, MessageSquare, Link2, Check, X, RotateCcw, MapPin, ArrowLeftRight, RotateCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCustomerName } from "@/lib/clientUtils";
import { useFloorPlanDrag } from "@/contexts/FloorPlanDragContext";
import { FloorPlanZone } from "@/hooks/useFloorPlanZones";

interface FloorPlanTableProps {
  position: TablePosition;
  table: Table | CombinedTable;
  status: TableStatus;
  reservation?: Reservation;
  cellSize: number;
  isEditMode: boolean;
  isDropTarget?: boolean;
  isValidDropTarget?: boolean;
  isSwapDropTarget?: boolean;
  isCombinedDropTarget?: boolean;
  combinedDropTargetName?: string;
  onPositionChange: (newPosition: TablePosition) => boolean | void;
  onEditReservation?: (reservation: Reservation) => void;
  onStatusChange?: (reservation: Reservation, status: "arrivé" | "annulé" | "planifié") => void;
  onDrop?: (tableId: string, tableType: 'single' | 'combined') => void;
  linkedTableInfo?: LinkedTableInfo;
  gridCols?: number;
  gridRows?: number;
  isHighlighted?: boolean;
  checkCollision?: (newPosition: TablePosition, excludeTableId?: string) => boolean;
  freeMovementMode?: boolean;
  zones?: FloorPlanZone[];
  onAssignToZone?: (tableId: string, zoneId: string | null) => void;
  onDragMove?: (tableId: string, offsetX: number, offsetY: number) => void;
  onDragEnd?: (tableId: string) => void;
}

const getCurrentShape = (width: number, height: number, savedShape?: TableShape): TableShape => {
  if (savedShape === 'round') return 'round';
  if (width === height) return 'square';
  if (width > height) return 'horizontal';
  return 'vertical';
};

export const FloorPlanTable = ({
  position,
  table,
  status,
  reservation,
  cellSize,
  isEditMode,
  isDropTarget = false,
  isValidDropTarget = false,
  isSwapDropTarget = false,
  isCombinedDropTarget = false,
  combinedDropTargetName,
  onPositionChange,
  onEditReservation,
  onStatusChange,
  onDrop,
  linkedTableInfo,
  gridCols = 12,
  gridRows = 8,
  isHighlighted = false,
  checkCollision,
  freeMovementMode = false,
  zones = [],
  onAssignToZone,
  onDragMove,
  onDragEnd: onDragEndCallback,
}: FloorPlanTableProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [hasCollision, setHasCollision] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [resizeOffset, setResizeOffset] = useState<{ dw: number; dh: number }>({ dw: 0, dh: 0 });

  // Get current zone color for border indicator
  const currentZone = zones.find(z => z.id === position.zoneId);
  const zoneColor = currentZone?.color;
  const [isEditHovering, setIsEditHovering] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const { dragState, startDrag: startReservationDrag, endDrag: endReservationDrag, setHoveredTable } = useFloorPlanDrag();
  
  // Long press timer for mobile reservation drag
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isReservationDragging, setIsReservationDragging] = useState(false);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const currentShape = getCurrentShape(position.width, position.height, position.shape);
  const isRound = currentShape === 'round';
  

  // Debounced hover for edit popover
  const handleEditMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsEditHovering(true);
  };

  const handleEditMouseLeave = () => {
    if (isDragging) return; // Don't close popover during drag
    hoverTimeoutRef.current = setTimeout(() => {
      setIsEditHovering(false);
    }, 150);
  };

  const rotationDeg = position.rotation || 0;

  const style: React.CSSProperties = {
    left: `${position.x * cellSize}px`,
    top: `${position.y * cellSize}px`,
    width: `${(position.width * cellSize - 4) + (isResizing ? resizeOffset.dw : 0)}px`,
    height: `${(position.height * cellSize - 4) + (isResizing ? resizeOffset.dh : 0)}px`,
    ...(isDragging ? {
      transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(1.05) rotate(${rotationDeg}deg)`,
      willChange: 'transform' as const,
      transition: 'none',
      zIndex: 100,
    } : {
      transform: rotationDeg ? `rotate(${rotationDeg}deg)` : undefined,
    }),
  };

  // Shared drag logic for both mouse and touch
  const startDrag = (clientX: number, clientY: number) => {
    setIsDragging(true);
    setHasCollision(false);
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      origX: position.x,
      origY: position.y,
    };
  };

  const moveDrag = (clientX: number, clientY: number) => {
    if (!dragRef.current) return;
    // Raw pixel delta for fluid visual movement
    const rawDeltaX = clientX - dragRef.current.startX;
    const rawDeltaY = clientY - dragRef.current.startY;
    setDragOffset({ x: rawDeltaX, y: rawDeltaY });
    onDragMove?.(position.tableId, rawDeltaX, rawDeltaY);
  };

  const endDrag = () => {
    if (dragRef.current) {
      // Snap to nearest grid cell on release
      const deltaX = Math.round(dragOffset.x / cellSize);
      const deltaY = Math.round(dragOffset.y / cellSize);
      const newX = Math.max(0, dragRef.current.origX + deltaX);
      const newY = Math.max(0, dragRef.current.origY + deltaY);

      if (newX !== position.x || newY !== position.y) {
        const newPosition = { ...position, x: newX, y: newY };
        if (checkCollision && !freeMovementMode) {
          const wouldCollide = checkCollision(newPosition, position.tableId);
          setHasCollision(wouldCollide);
          if (wouldCollide) {
            setTimeout(() => setHasCollision(false), 1500);
          }
        }
        onPositionChange(newPosition);
      }
    }
    setIsDragging(false);
    setHasCollision(false);
    setDragOffset({ x: 0, y: 0 });
    onDragEndCallback?.(position.tableId);
    dragRef.current = null;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditMode) return;
    // Don't start drag if clicking on size buttons
    if ((e.target as HTMLElement).closest('.size-buttons')) return;
    
    e.preventDefault();
    startDrag(e.clientX, e.clientY);

    const handleMouseMove = (e: MouseEvent) => {
      moveDrag(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      endDrag();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isEditMode) return;
    // Don't start drag if touching on size buttons
    if ((e.target as HTMLElement).closest('.size-buttons')) return;
    
    const touch = e.touches[0];
    e.preventDefault(); // Prevent scroll during drag
    startDrag(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragRef.current || !isEditMode) return;
    const touch = e.touches[0];
    moveDrag(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    endDrag();
  };

  const handleShapeChange = (shape: TableShape) => {
    const newPosition = { ...position, shape };
    if (checkCollision && !freeMovementMode) {
      const wouldCollide = checkCollision(newPosition, position.tableId);
      if (wouldCollide) {
        setHasCollision(true);
        setTimeout(() => setHasCollision(false), 1500);
      }
    }
    onPositionChange(newPosition);
  };

  const handleRotate = () => {
    const newRotation = ((position.rotation || 0) + 45) % 360;
    onPositionChange({ ...position, rotation: newRotation });
  };

  // Resize handlers
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const origW = position.width;
    const origH = position.height;
    let latestDw = 0;
    let latestDh = 0;

    const handleResizeMove = (ev: MouseEvent) => {
      latestDw = ev.clientX - startX;
      latestDh = ev.clientY - startY;
      setResizeOffset({ dw: latestDw, dh: latestDh });
    };

    const handleResizeUp = () => {
      const finalW = Math.max(1, Math.min(4, Math.round(origW + latestDw / cellSize)));
      const finalH = Math.max(1, Math.min(4, Math.round(origH + latestDh / cellSize)));
      const newPosition = { ...position, width: finalW, height: finalH };
      if (checkCollision && !freeMovementMode) {
        const wouldCollide = checkCollision(newPosition, position.tableId);
        if (wouldCollide) {
          setHasCollision(true);
          setTimeout(() => setHasCollision(false), 1500);
        }
      }
      onPositionChange(newPosition);
      setIsResizing(false);
      setResizeOffset({ dw: 0, dh: 0 });
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeUp);
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeUp);
  };

  // Drag & drop handlers for reservation movement
  const handleDragOver = (e: React.DragEvent) => {
    // Always prevent default to allow drop
    e.preventDefault();
    e.stopPropagation();
    
    if (isEditMode) return;
    
    // Accept drag over valid targets OR swap targets
    e.dataTransfer.dropEffect = (isValidDropTarget || isSwapDropTarget) ? 'move' : 'none';
    if (!isHovering) {
      setIsHovering(true);
      setHoveredTable(table.id);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only handle if leaving the table element itself
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsHovering(false);
      setHoveredTable(null);
    }
  };

  const handleDropOnTable = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsHovering(false);
    setHoveredTable(null);

    // Get reservation ID from dataTransfer to verify drag is active
    const reservationId = e.dataTransfer.getData('text/plain');
    // Accept drops on valid targets OR swap targets (occupied tables for swap)
    if (reservationId && (isValidDropTarget || isSwapDropTarget) && onDrop) {
      onDrop(table.id, position.type);
    }
  };

  // Determine visual feedback for drop targets
  const showDropFeedback = dragState.isDragging && !isEditMode;
  const isCurrentlyHovered = isHovering || dragState.hoveredTableId === table.id;

  // Note: Zone assignment via tabs is now handled by context menu / popover only
  
  // Drag handler for reservation movement from table (non-edit mode)
  const handleReservationDragStart = (e: React.DragEvent) => {
    if (isEditMode || !reservation || reservation.status === 'annulé') {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', reservation.id);
    startReservationDrag(reservation);
    setIsReservationDragging(true);
  };
  
  const handleReservationDragEnd = () => {
    endReservationDrag();
    setIsReservationDragging(false);
  };
  
  // Touch handlers for reservation drag (long press to initiate)
  const handleReservationTouchStart = (e: React.TouchEvent) => {
    if (isEditMode || !reservation || reservation.status === 'annulé') return;
    
    const touch = e.touches[0];
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
    
    // Long press (500ms) to start dragging
    longPressTimerRef.current = setTimeout(() => {
      setIsReservationDragging(true);
      startReservationDrag(reservation);
      // Haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(50);
      // Prevent popover from opening
      setPopoverOpen(false);
    }, 500);
  };
  
  const handleReservationTouchMove = (e: React.TouchEvent) => {
    // Cancel long press if user moves finger significantly
    if (longPressTimerRef.current && touchStartPosRef.current) {
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const dy = Math.abs(touch.clientY - touchStartPosRef.current.y);
      if (dx > 10 || dy > 10) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  };
  
  const handleReservationTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (isReservationDragging) {
      endReservationDrag();
      setIsReservationDragging(false);
    }
    touchStartPosRef.current = null;
  };
  
  // Check if this table is the source of the current drag
  const isSourceOfDrag = dragState.isDragging && dragState.draggedReservation?.id === reservation?.id;
  
  // Can this reservation be dragged?
  const canDragReservation = !isEditMode && !!reservation && reservation.status !== 'annulé';

  const tableContent = (
    <div
      className={cn(
        "absolute z-20",
        // Source of drag styling
        isSourceOfDrag && "opacity-50 scale-95",
        // Cursor for draggable reservation
        canDragReservation && !isSourceOfDrag && "cursor-grab active:cursor-grabbing"
      )}
      style={style}
      data-floor-plan-table="true"
      draggable={canDragReservation && !isEditMode}
      onDragStart={!isEditMode ? handleReservationDragStart : undefined}
      onDragEnd={!isEditMode ? handleReservationDragEnd : undefined}
      onMouseEnter={isEditMode ? handleEditMouseEnter : undefined}
      onMouseLeave={isEditMode ? handleEditMouseLeave : undefined}
      onMouseDown={isEditMode ? handleMouseDown : undefined}
      onTouchStart={isEditMode ? handleTouchStart : (canDragReservation ? handleReservationTouchStart : undefined)}
      onTouchMove={isEditMode ? handleTouchMove : (canDragReservation ? handleReservationTouchMove : undefined)}
      onTouchEnd={isEditMode ? handleTouchEnd : (canDragReservation ? handleReservationTouchEnd : undefined)}
    >
      {/* Floating controls bar - using Popover to escape overflow:hidden */}
      {isEditMode && (
        <Popover open={isEditHovering} onOpenChange={() => {}}>
          <PopoverTrigger asChild>
            <div className="absolute inset-0 pointer-events-none" />
          </PopoverTrigger>
          <PopoverContent
            side={position.y < 1 ? "bottom" : "top"}
            align={position.x < 2 ? "start" : position.x + position.width > gridCols - 2 ? "end" : "center"}
            sideOffset={8}
            className="w-auto p-1.5 flex items-center gap-2 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg"
            onMouseEnter={handleEditMouseEnter}
            onMouseLeave={handleEditMouseLeave}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {/* Table name */}
            <div className="text-xs font-semibold text-foreground px-1 border-r pr-2 min-w-0 max-w-[120px] truncate" title={table.name}>
              {table.name}
              <span className="text-muted-foreground font-normal ml-1">
                {'capacity' in table ? table.capacity : table.totalCapacity}p
              </span>
            </div>
            
            {/* Shape buttons */}
            <div className="size-buttons flex gap-0.5 border-r pr-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleShapeChange('square');
                }}
                className={cn(
                  "w-6 h-6 flex items-center justify-center rounded transition-colors",
                  currentShape === 'square' 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
                title="Carré"
              >
                <div className="w-3 h-3 border-2 border-current" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleShapeChange('round');
                }}
                className={cn(
                  "w-6 h-6 flex items-center justify-center rounded transition-colors",
                  currentShape === 'round' 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
                title="Rond"
              >
                <div className="w-3 h-3 border-2 border-current rounded-full" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleShapeChange('horizontal');
                }}
                className={cn(
                  "w-6 h-6 flex items-center justify-center rounded transition-colors",
                  currentShape === 'horizontal' 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
                title="Rectangle horizontal"
              >
                <div className="w-3.5 h-2 border-2 border-current" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleShapeChange('vertical');
                }}
                className={cn(
                  "w-6 h-6 flex items-center justify-center rounded transition-colors",
                  currentShape === 'vertical' 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
                title="Rectangle vertical"
              >
                <div className="w-2 h-3.5 border-2 border-current" />
              </button>
            </div>
            
            {/* Rotation button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRotate();
              }}
              className="w-6 h-6 flex items-center justify-center rounded transition-colors bg-muted text-muted-foreground hover:bg-muted/80"
              title={`Rotation (${position.rotation || 0}°)`}
            >
              <RotateCw className="w-3.5 h-3.5" />
            </button>

            {/* Zone assignment button */}
            {zones.length > 0 && onAssignToZone && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    className={cn(
                      "w-6 h-6 flex items-center justify-center rounded transition-colors border-l ml-1 pl-1",
                      currentZone
                        ? "text-foreground"
                        : "text-muted-foreground hover:bg-muted/80"
                    )}
                    title="Assigner à une zone"
                  >
                    <MapPin className="w-3.5 h-3.5" style={{ color: zoneColor || undefined }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="center" 
                  side={position.y < 1 ? "bottom" : "top"}
                  onPointerDownOutside={(e) => e.preventDefault()}
                  onInteractOutside={(e) => e.preventDefault()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuItem
                    onClick={() => onAssignToZone(table.id, null)}
                    className={cn(!currentZone && "bg-accent")}
                  >
                    <span className="w-2 h-2 rounded-full mr-2 bg-muted-foreground/30" />
                    Aucune zone
                  </DropdownMenuItem>
                  {zones.map(zone => (
                    <DropdownMenuItem
                      key={zone.id}
                      onClick={() => onAssignToZone(table.id, zone.id)}
                      className={cn(currentZone?.id === zone.id && "bg-accent")}
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-2 shrink-0"
                        style={{ backgroundColor: zone.color }}
                      />
                      {zone.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </PopoverContent>
        </Popover>
      )}
      
      {/* Table itself - wrapped in tooltip */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "w-full h-full border-2 transition-all flex flex-col items-center justify-center text-center",
                (() => { const d = Math.min(position.width, position.height) * cellSize; return d < 35 ? "p-0.5" : d < 50 ? "p-1" : "p-2"; })(),
                isRound ? "rounded-full" : "rounded-lg",
                getStatusColor(status),
                isDragging && !hasCollision && "opacity-80 shadow-xl z-50",
                isDragging && hasCollision && "opacity-50 shadow-xl z-50 ring-4 ring-red-500 border-red-500",
                isEditMode && "cursor-move",
                isEditMode && "border-dashed",
                isEditMode && isEditHovering && !hasCollision && "shadow-lg border-primary/50",
                hasCollision && !isDragging && "ring-2 ring-red-500 animate-pulse",
                !isEditMode && reservation && !isSourceOfDrag && "cursor-pointer hover:shadow-md",
                linkedTableInfo?.isCombined && !showDropFeedback && "ring-2 ring-purple-500/50 ring-offset-1",
                // "Hors réservation" table styling
                !('tableIds' in table) && (table as any).isBookable === false && "border-amber-500 border-dashed",
                // Source of drag feedback - dimmed with ring
                isSourceOfDrag && "ring-2 ring-primary shadow-inner",
                // Drop target feedback - Combined tables get purple styling
                showDropFeedback && isCombinedDropTarget && "ring-4 ring-purple-500 animate-pulse shadow-lg shadow-purple-500/40",
                showDropFeedback && isCombinedDropTarget && isCurrentlyHovered && "ring-4 ring-purple-400 scale-105 shadow-xl shadow-purple-500/50",
                // Drop target feedback - Swap targets get orange styling
                showDropFeedback && isSwapDropTarget && !isCombinedDropTarget && "ring-2 ring-orange-500/50",
                showDropFeedback && isSwapDropTarget && !isCombinedDropTarget && isCurrentlyHovered && "ring-4 ring-orange-500 scale-105 shadow-lg shadow-orange-500/30",
                // Drop target feedback - Single tables (free) get green styling
                showDropFeedback && isValidDropTarget && !isCombinedDropTarget && !isSwapDropTarget && "ring-2 ring-green-500/50",
                showDropFeedback && isValidDropTarget && !isCombinedDropTarget && !isSwapDropTarget && isCurrentlyHovered && "ring-4 ring-green-500 scale-105 shadow-lg shadow-green-500/30",
                // Dimmed feedback for invalid targets (not valid, not swap, not combined)
                showDropFeedback && !isValidDropTarget && !isCombinedDropTarget && !isSwapDropTarget && !isSourceOfDrag && "opacity-40",
                // Highlighted from search
                isHighlighted && "ring-4 ring-yellow-500 animate-pulse shadow-lg shadow-yellow-500/40"
              )}
              style={{
                // Zone color indicator as left border
                borderLeftColor: zoneColor || undefined,
                borderLeftWidth: zoneColor ? 4 : undefined,
              }}
              
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDropOnTable}
            >
              {/* Resize handle - bottom-right only */}
              {isEditMode && isEditHovering && !isDragging && (
                <div
                  className="absolute -bottom-1 -right-1 w-3 h-3 bg-primary border border-primary-foreground rounded-sm cursor-nwse-resize z-30 hover:scale-125 transition-transform flex items-center justify-center"
                  onMouseDown={handleResizeMouseDown}
                >
                  <svg width="6" height="6" viewBox="0 0 6 6" className="text-primary-foreground">
                    <line x1="5" y1="1" x2="1" y2="5" stroke="currentColor" strokeWidth="1" />
                    <line x1="5" y1="3" x2="3" y2="5" stroke="currentColor" strokeWidth="1" />
                  </svg>
                </div>
              )}
              {/* Zone indicator dot */}
              {zoneColor && !isEditMode && Math.min(position.width, position.height) * cellSize >= 40 && (
                <span 
                  className="absolute top-1 left-1 w-2 h-2 rounded-full"
                  style={{ backgroundColor: zoneColor }}
                  title={currentZone?.name}
                />
              )}
              
              {/* Grip icon for edit mode */}
              {isEditMode && Math.min(position.width, position.height) * cellSize >= 40 && (
                <GripVertical className={cn(
                  "absolute top-1 right-1 w-3 h-3 transition-opacity",
                  isEditHovering ? "opacity-60" : "opacity-30"
                )} />
              )}
              
              {/* Link icon for combined tables */}
              {linkedTableInfo?.isCombined && !isEditMode && !showDropFeedback && (
                <Link2 className="absolute top-1 right-1 w-3 h-3 text-purple-500" />
              )}
              
              {/* Badge for "hors réservation" tables */}
              {!('tableIds' in table) && (table as any).isBookable === false && !isEditMode && (
                <Badge className="absolute -top-2 -right-2 text-[8px] px-1 py-0 bg-amber-500 hover:bg-amber-500">
                  HR
                </Badge>
              )}
              
              {/* Combined drop target indicator */}
              {showDropFeedback && isCombinedDropTarget && combinedDropTargetName && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <Badge className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 shadow-md">
                    {combinedDropTargetName}
                  </Badge>
                </div>
              )}
              
              {/* Swap drop target indicator */}
              {showDropFeedback && isSwapDropTarget && !isCombinedDropTarget && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0.5 shadow-md flex items-center gap-1">
                    <ArrowLeftRight className="w-3 h-3" />
                    Échanger
                  </Badge>
                </div>
              )}
              
              {(() => {
                const minDimPx = Math.min(position.width, position.height) * cellSize;
                const showDetails = minDimPx >= 35;
                return (
                  <>
                    <div 
                      className={cn(
                        "font-semibold w-full text-center leading-tight",
                        minDimPx >= 50 ? "truncate" : "overflow-visible whitespace-nowrap"
                      )}
                      style={{
                        fontSize: `clamp(10px, ${minDimPx * 0.3}px, 14px)`,
                      }}
                    >
                      {table.name}
                    </div>
                    
                    {reservation && status !== 'free' ? (
                      <>
                        {showDetails && (
                          <div 
                            className="font-medium truncate w-full text-center"
                            style={{
                              fontSize: `clamp(7px, ${minDimPx * 0.18}px, 12px)`,
                            }}
                          >
                            {position.width === 1 
                              ? `${reservation.customerFirstName?.charAt(0) || ''}${reservation.customerLastName?.charAt(0) ? `. ${reservation.customerLastName.charAt(0)}.` : '.'}`
                              : `${formatCustomerName(reservation.customerFirstName, reservation.customerLastName?.charAt(0) ? `${reservation.customerLastName.charAt(0)}.` : undefined)}`
                            }
                          </div>
                        )}
                        {showDetails && (
                          <div
                            className="flex items-center justify-center gap-0.5 opacity-80 w-full"
                            style={{
                              fontSize: `clamp(6px, ${minDimPx * 0.15}px, 10px)`,
                            }}
                          >
                            <span>{reservation.startTime.substring(0, 5)}</span>
                            <span className="mx-0.5">·</span>
                            <span>{reservation.numberOfPeople}/{'capacity' in table ? table.capacity : table.totalCapacity}</span>
                          </div>
                        )}
                        {showDetails && linkedTableInfo?.isCombined && linkedTableInfo.combinedTableName && (
                          <div
                            className="flex items-center justify-center gap-0.5 text-purple-500 dark:text-purple-400"
                            style={{
                              fontSize: `clamp(6px, ${minDimPx * 0.13}px, 9px)`,
                            }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 inline-block shrink-0" />
                            <span className="truncate">{linkedTableInfo.combinedTableName}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      showDetails && (
                        <div
                          className="text-muted-foreground text-center"
                          style={{
                            fontSize: `clamp(7px, ${minDimPx * 0.18}px, 11px)`,
                          }}
                        >
                          {'capacity' in table ? table.capacity : table.totalCapacity}p
                        </div>
                      )
                    )}
                  </>
                );
              })()}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="font-medium">
            {table.name} • {'capacity' in table ? table.capacity : table.totalCapacity} places
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  // Edit mode: wrap in context menu for zone assignment
  if (isEditMode) {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {tableContent}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          {zones.length > 0 && onAssignToZone && (
            <>
              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <MapPin className="w-4 h-4 mr-2" />
                  Déplacer vers zone
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-48">
                  <ContextMenuItem
                    onClick={() => onAssignToZone(table.id, null)}
                    className={cn(!currentZone && "bg-accent")}
                  >
                    <span className="w-2 h-2 rounded-full mr-2 bg-muted-foreground/30" />
                    Aucune zone
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  {zones.map(zone => (
                    <ContextMenuItem
                      key={zone.id}
                      onClick={() => onAssignToZone(table.id, zone.id)}
                      className={cn(currentZone?.id === zone.id && "bg-accent")}
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-2 shrink-0"
                        style={{ backgroundColor: zone.color }}
                      />
                      {zone.name}
                    </ContextMenuItem>
                  ))}
                </ContextMenuSubContent>
              </ContextMenuSub>
            </>
          )}
          {zones.length === 0 && (
            <ContextMenuItem disabled>
              <MapPin className="w-4 h-4 mr-2 opacity-50" />
              Aucune zone disponible
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  }
  
  // No reservation: just show table
  if (!reservation) {
    return tableContent;
  }

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        {tableContent}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="center">
        <div className="p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold">
                {reservation.customerFirstName} {reservation.customerLastName}
              </h4>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>{reservation.numberOfPeople} personne{reservation.numberOfPeople > 1 ? 's' : ''}</span>
              </div>
            </div>
            <Badge variant={status === 'arrived' ? 'default' : status === 'late' ? 'destructive' : 'secondary'}>
              {status === 'arrived' ? 'Arrivé' : status === 'late' ? 'En retard' : 'Planifié'}
            </Badge>
          </div>
          
          {/* Combined table info */}
          {linkedTableInfo?.isCombined && (
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-purple-500/10 border border-purple-500/30">
              <Link2 className="w-4 h-4 text-purple-500" />
              <span className="text-sm text-purple-700 dark:text-purple-300">
                Combinée avec {linkedTableInfo.linkedTableNames.join(', ')}
              </span>
            </div>
          )}
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{reservation.startTime.substring(0, 5)}</span>
            </div>
            
            {reservation.customerPhone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{reservation.customerPhone}</span>
              </div>
            )}
            
            {reservation.customerEmail && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="truncate">{reservation.customerEmail}</span>
              </div>
            )}
            
            
            {reservation.notes && (
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5" />
                <span className="text-muted-foreground">{reservation.notes}</span>
              </div>
            )}
          </div>
          
          {/* Quick action buttons */}
          {onStatusChange && (
            <div className="flex gap-2">
              {reservation.status !== 'arrivé' && (
                <Button 
                  variant="default"
                  size="sm" 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    onStatusChange(reservation, 'arrivé');
                    setPopoverOpen(false);
                  }}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Arrivé
                </Button>
              )}
              {reservation.status !== 'annulé' && (
                <Button 
                  variant="destructive"
                  size="sm" 
                  className="flex-1"
                  onClick={() => {
                    onStatusChange(reservation, 'annulé');
                    setPopoverOpen(false);
                  }}
                >
                  <X className="w-4 h-4 mr-1" />
                  Annuler
                </Button>
              )}
              {(reservation.status === 'arrivé' || reservation.status === 'annulé') && (
                <Button 
                  variant="outline"
                  size="sm" 
                  className="flex-1"
                  onClick={() => {
                    onStatusChange(reservation, 'planifié');
                    setPopoverOpen(false);
                  }}
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Planifié
                </Button>
              )}
            </div>
          )}
          
          {onEditReservation && (
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => {
                setPopoverOpen(false);
                onEditReservation(reservation);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifier la réservation
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
